import React, { useEffect, useState } from 'react';
import { useAuth } from './AuthContext';
import { useNavigate } from 'react-router-dom';

const PlanSelectionSuccess = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Fetch current subscription details
    const fetchSubscription = async () => {
      try {
        const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
        const response = await fetch(`${backendUrl}/api/subscription/current`, {
          credentials: 'include',
        });
        
        if (response.ok) {
          const data = await response.json();
          setSubscription(data);
        }
      } catch (error) {
        console.error('Error fetching subscription:', error);
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      fetchSubscription();
    }
  }, [user]);

  const handleContinue = () => {
    navigate('/');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-white">Loading...</div>
      </div>
    );
  }

  const planName = subscription?.subscription?.subscription_name || 'Explorer';
  const planPrice = subscription?.package_config?.price || 0;

  return (
    <div className="min-h-screen bg-gray-900 flex items-center justify-center p-4">
      <div className="max-w-2xl w-full">
        <div className="bg-gray-800 rounded-2xl p-8 text-center">
          {/* Success Icon */}
          <div className="w-20 h-20 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>

          {/* Success Message */}
          <h1 className="text-3xl font-bold text-white mb-4">
            Welcome to {planName}!
          </h1>
          
          <p className="text-gray-300 text-lg mb-8">
            {planPrice === 0 
              ? "You're all set with your free plan. Start exploring our AI-powered immigration tools."
              : `Your ${planName} subscription is now active. You have access to all premium features.`
            }
          </p>

          {/* Plan Features */}
          <div className="bg-gray-700 rounded-lg p-6 mb-8">
            <h3 className="text-xl font-semibold text-white mb-4">Your Plan Includes:</h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div className="text-gray-300">
                <span className="text-green-400">✓</span> AI Interactions: {subscription?.package_config?.features?.ai_interactions === 999999 ? 'Unlimited' : subscription?.package_config?.features?.ai_interactions || '10'}
              </div>
              <div className="text-gray-300">
                <span className="text-green-400">✓</span> Document Uploads: {subscription?.package_config?.features?.document_uploads === 999999 ? 'Unlimited' : subscription?.package_config?.features?.document_uploads || '1'}
              </div>
              <div className="text-gray-300">
                <span className="text-green-400">✓</span> Document Scans: {subscription?.package_config?.features?.document_scans === 999999 ? 'Unlimited' : subscription?.package_config?.features?.document_scans || '1'}
              </div>
              <div className="text-gray-300">
                <span className="text-green-400">✓</span> AI Agents: {subscription?.package_config?.features?.ai_agents || '1'}
              </div>
            </div>
          </div>

          {/* Next Steps */}
          <div className="text-left mb-8">
            <h3 className="text-xl font-semibold text-white mb-4">Next Steps:</h3>
            <ul className="space-y-2 text-gray-300">
              <li className="flex items-start">
                <span className="text-blue-400 mr-2">1.</span>
                <span>Explore our AI agents for different immigration cases</span>
              </li>
              <li className="flex items-start">
                <span className="text-blue-400 mr-2">2.</span>
                <span>Upload your documents for AI analysis</span>
              </li>
              <li className="flex items-start">
                <span className="text-blue-400 mr-2">3.</span>
                <span>Get personalized recommendations for your case</span>
              </li>
              <li className="flex items-start">
                <span className="text-blue-400 mr-2">4.</span>
                <span>Generate professional document packages</span>
              </li>
            </ul>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={handleContinue}
              className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 rounded-lg font-medium transition-colors"
            >
              Start Using Your Plan
            </button>
            <button
              onClick={() => navigate('/profile')}
              className="bg-gray-600 hover:bg-gray-700 text-white px-8 py-3 rounded-lg font-medium transition-colors"
            >
              View Account Settings
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlanSelectionSuccess;